import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { resolveClinicForUser } from '../lib/clinicResolver';
import { WAREHOUSE_UNITS, WAREHOUSE_CATEGORIES } from '../lib/constants';
import Sidebar from './Sidebar';
import LoadingSpinner from './LoadingSpinner';
import { useWarehouse } from '../contexts/WarehouseContext';

const WarehousePage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [clinic, setClinic] = useState(null);
  
  // Use global warehouse context
  const { items, fetchItems } = useWarehouse();
  const [filteredItems, setFilteredItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [showLowStockOnly, setShowLowStockOnly] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showAddItemsDialog, setShowAddItemsDialog] = useState(false);
  const [itemToAddTo, setItemToAddTo] = useState(null);
  const [addQuantity, setAddQuantity] = useState('');
  const [showBulkAddDialog, setShowBulkAddDialog] = useState(false);
  const [bulkAddQuantities, setBulkAddQuantities] = useState({});
  const [bulkAddSearchTerm, setBulkAddSearchTerm] = useState('');
  const [bulkAddError, setBulkAddError] = useState('');
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const toastTimeoutRef = useRef(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Form for adding multiple items
  const [newItems, setNewItems] = useState([
    { name: '', description: '', quantity: '', unit: 'τεμάχια', min_quantity: '', category: '' }
  ]);

  const units = WAREHOUSE_UNITS;
  const categories = WAREHOUSE_CATEGORIES;

  // Cleanup toast timeout on unmount
  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    };
  }, []);

  const showToast = (message, type = 'success') => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToast({ show: true, message, type });
    toastTimeoutRef.current = setTimeout(() => {
      setToast({ show: false, message: '', type: 'success' });
    }, 4000);
  };

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    if (clinic && user) {
      fetchItems();
    }
  }, [clinic, user]);

  useEffect(() => {
    filterAndSortItems();
    setCurrentPage(1); // Reset to page 1 when filters change
  }, [items, searchTerm, sortBy, sortOrder, showLowStockOnly]);

  const init = async () => {
    try {
      const { data: { user: u } } = await supabase.auth.getUser();
      if (!u) { navigate('/login'); return; }
      setUser(u);
      
      const { clinic: clinicData } = await resolveClinicForUser(supabase, u.id);
      setClinic(clinicData || null);
    } catch {
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortItems = () => {
    let filtered = [...items];

    // Filter by search term
    if (searchTerm.trim()) {
      filtered = filtered.filter(item =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.category && item.category.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Filter by low stock
    if (showLowStockOnly) {
      filtered = filtered.filter(item => 
        parseFloat(item.quantity) <= parseFloat(item.min_quantity || 0) && parseFloat(item.min_quantity) > 0
      );
    }

    // Sort
    filtered.sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];

      if (sortBy === 'quantity' || sortBy === 'min_quantity') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else {
        aVal = (aVal || '').toString().toLowerCase();
        bVal = (bVal || '').toString().toLowerCase();
      }

      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    setFilteredItems(filtered);
  };

  const addItemRow = () => {
    setNewItems([...newItems, { name: '', description: '', quantity: '', unit: 'τεμάχια', min_quantity: '', category: '' }]);
  };

  const removeItemRow = (index) => {
    setNewItems(newItems.filter((_, i) => i !== index));
  };

  const updateItemRow = (index, field, value) => {
    const updated = [...newItems];
    updated[index][field] = value;
    setNewItems(updated);
  };

  const saveItems = async () => {
    try {
      // Validate
      const validItems = newItems.filter(item => 
        item.name.trim() && 
        item.quantity && 
        !isNaN(parseFloat(item.quantity)) && 
        parseFloat(item.quantity) >= 0
      );
      
      if (validItems.length === 0) {
        showToast('Συμπληρώστε τουλάχιστον ένα προϊόν με όνομα και έγκυρη ποσότητα', 'error');
        return;
      }

      // Prepare data
      const itemsToInsert = validItems.map(item => ({
        clinic_id: clinic.id,
        name: item.name.trim(),
        description: item.description.trim() || null,
        quantity: parseFloat(item.quantity),
        unit: item.unit,
        min_quantity: parseFloat(item.min_quantity) || 0,
        category: item.category || null,
      }));

      const { error } = await supabase
        .from('warehouse_items')
        .insert(itemsToInsert);

      if (error) throw error;

      showToast(`Προστέθηκαν ${validItems.length} προϊόντα`, 'success');
      setNewItems([{ name: '', description: '', quantity: '', unit: 'τεμάχια', min_quantity: '', category: '' }]);
      setShowAddForm(false);
      await fetchItems();
    } catch (err) {
      console.error('Error saving items:', err);
      showToast('Σφάλμα προσθήκης προϊόντων', 'error');
    }
  };

  const updateQuantity = async (itemId, newQuantity) => {
    try {
      const { error } = await supabase
        .from('warehouse_items')
        .update({ quantity: parseFloat(newQuantity) })
        .eq('id', itemId);

      if (error) throw error;
      await fetchItems();
      showToast('Η ποσότητα ενημερώθηκε', 'success');
    } catch (err) {
      console.error('Error updating quantity:', err);
      showToast('Σφάλμα ενημέρωσης', 'error');
    }
  };

  const deleteItem = async (itemId) => {
    try {
      const { error } = await supabase
        .from('warehouse_items')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', itemId);

      if (error) throw error;
      await fetchItems();
      showToast('Το προϊόν διαγράφηκε', 'success');
      setShowDeleteDialog(false);
      setItemToDelete(null);
    } catch (err) {
      console.error('Error deleting item:', err);
      showToast('Σφάλμα διαγραφής', 'error');
    }
  };

  const confirmDelete = (item) => {
    setItemToDelete(item);
    setShowDeleteDialog(true);
  };

  const editItem = (item) => {
    setEditingItem(item);
  };

  const saveEdit = async () => {
    if (!editingItem) return;

    // Validate quantity
    if (!editingItem.quantity || isNaN(parseFloat(editingItem.quantity)) || parseFloat(editingItem.quantity) < 0) {
      showToast('Παρακαλώ εισάγετε έγκυρη ποσότητα', 'error');
      return;
    }

    try {
      const { error } = await supabase
        .from('warehouse_items')
        .update({
          name: editingItem.name,
          description: editingItem.description,
          quantity: parseFloat(editingItem.quantity),
          unit: editingItem.unit,
          min_quantity: parseFloat(editingItem.min_quantity) || 0,
          category: editingItem.category,
        })
        .eq('id', editingItem.id);

      if (error) throw error;
      await fetchItems();
      setEditingItem(null);
      showToast('Το προϊόν ενημερώθηκε', 'success');
    } catch (err) {
      console.error('Error updating item:', err);
      showToast('Σφάλμα ενημέρωσης', 'error');
    }
  };

  const cancelEdit = () => {
    setEditingItem(null);
  };

  const addQuantityToItem = async () => {
    if (!itemToAddTo || !addQuantity || isNaN(parseFloat(addQuantity)) || parseFloat(addQuantity) <= 0) {
      showToast('Παρακαλώ εισάγετε έγκυρη ποσότητα', 'error');
      return;
    }

    try {
      const currentQuantity = parseFloat(itemToAddTo.quantity);
      const quantityToAdd = parseFloat(addQuantity);
      const newQuantity = currentQuantity + quantityToAdd;

      const { error } = await supabase
        .from('warehouse_items')
        .update({ 
          quantity: newQuantity,
          updated_at: new Date().toISOString()
        })
        .eq('id', itemToAddTo.id);

      if (error) throw error;

      showToast(`Προστέθηκαν ${addQuantity} ${itemToAddTo.unit} στο ${itemToAddTo.name}`, 'success');
      await fetchItems();
      setShowAddItemsDialog(false);
      setItemToAddTo(null);
      setAddQuantity('');
    } catch (error) {
      console.error('Error adding quantity:', error);
      showToast('Σφάλμα προσθήκης ποσότητας', 'error');
    }
  };

  const handleBulkAddQuantities = async () => {
    // Clear any previous errors
    setBulkAddError('');
    
    const validItems = Object.entries(bulkAddQuantities).filter(([itemId, quantity]) => 
      quantity && !isNaN(parseFloat(quantity)) && parseFloat(quantity) > 0
    );

    if (validItems.length === 0) {
      setBulkAddError('Παρακαλώ εισάγετε έγκυρες ποσότητες');
      // Auto-dismiss error after 5 seconds
      setTimeout(() => setBulkAddError(''), 5000);
      return;
    }

    try {
      let successCount = 0;
      for (const [itemId, quantity] of validItems) {
        const item = items.find(i => i.id === itemId);
        if (!item) continue;

        const currentQuantity = parseFloat(item.quantity);
        const quantityToAdd = parseFloat(quantity);
        const newQuantity = currentQuantity + quantityToAdd;

        const { error } = await supabase
          .from('warehouse_items')
          .update({ 
            quantity: newQuantity,
            updated_at: new Date().toISOString()
          })
          .eq('id', itemId);

        if (error) throw error;
        successCount++;
      }

      showToast(`Ενημερώθηκαν ${successCount} προϊόντα επιτυχώς`, 'success');
      await fetchItems();
      setShowBulkAddDialog(false);
      setBulkAddQuantities({});
      setBulkAddSearchTerm('');
      setBulkAddError('');
    } catch (error) {
      console.error('Error bulk adding quantities:', error);
      setBulkAddError('Σφάλμα ενημέρωσης προϊόντων');
      // Auto-dismiss error after 5 seconds
      setTimeout(() => setBulkAddError(''), 5000);
    }
  };

  const getLowStockCount = () => {
    return items.filter(item => parseFloat(item.quantity) <= parseFloat(item.min_quantity || 0) && parseFloat(item.min_quantity) > 0).length;
  };

  // Pagination helpers
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const paginatedItems = filteredItems.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen text-white flex flex-col lg:flex-row" style={{ backgroundColor: '#1A202C', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, background: 'radial-gradient(800px 800px at 10% -10%, rgba(16,185,129,0.12), transparent), radial-gradient(800px 800px at 110% 10%, rgba(59,130,246,0.08), transparent)' }} className="pointer-events-none" />

      <Sidebar 
        isSidebarOpen={isSidebarOpen} 
        setIsSidebarOpen={setIsSidebarOpen}
      />

      {/* Main Content */}
      <div className="flex-1 p-4 lg:p-8">
        {/* Toast */}
        {toast.show && (
          <div className="fixed top-4 right-4 left-4 sm:left-auto z-50">
            <div
              className="flex items-center px-5 py-4 rounded-xl border max-w-sm ml-auto transition-all duration-300"
              style={{
                background: 'linear-gradient(135deg, rgba(17,24,39,0.95) 0%, rgba(31,41,55,0.95) 100%)',
                backdropFilter: 'blur(10px)',
                borderColor: toast.type === 'success' ? '#10B981' : '#EF4444',
                boxShadow: `0 10px 25px rgba(0,0,0,0.3), 0 0 20px ${
                  toast.type === 'success' ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.2)'
                }`
              }}
            >
              {toast.type === 'success' && (
                <div className="w-8 h-8 rounded-full flex items-center justify-center mr-3" style={{ background: 'rgba(16,185,129,0.2)' }}>
                  <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              )}
              {toast.type === 'error' && (
                <div className="w-8 h-8 rounded-full flex items-center justify-center mr-3" style={{ background: 'rgba(239,68,68,0.2)' }}>
                  <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
              )}
              <span className="text-white font-medium flex-1">{toast.message}</span>
              <button
                onClick={() => setToast({ show: false, message: '', type: 'success' })}
                className="ml-3 text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="mb-6 lg:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-white mb-1">Αποθήκη</h1>
              <p className="text-gray-400 text-sm lg:text-base">Διαχείριση αποθέματος προϊόντων</p>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-xl" style={{ background: 'linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(6,182,212,0.15) 100%)', border: '1px solid rgba(16,185,129,0.3)' }}>
              <svg className="w-5 h-5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
              <span className="text-emerald-400 font-medium text-sm">
                {items.length} προϊόντα συνολικά
              </span>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          {/* Total Products Card */}
          <div
            className="rounded-xl p-5 border transition-all duration-300 hover:border-emerald-500/50 group"
            style={{
              background: 'linear-gradient(135deg, #111827 0%, #1F2937 100%)',
              borderColor: '#374151',
              boxShadow: '0 10px 25px rgba(0,0,0,0.3)'
            }}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="p-2.5 rounded-lg" style={{ backgroundColor: 'rgba(16,185,129,0.15)' }}>
                <svg className="w-6 h-6 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>
              <span className="text-xs px-2.5 py-1 rounded-full font-medium" style={{ backgroundColor: 'rgba(16,185,129,0.2)', color: '#10B981' }}>
                📦 Αποθήκη
              </span>
            </div>
            <p className="text-gray-400 text-sm mb-1">Σύνολο Προϊόντων</p>
            <span className="text-3xl font-bold text-white">{items.length}</span>
          </div>

          {/* Low Stock Card */}
          <div
            className="rounded-xl p-5 border transition-all duration-300 hover:border-red-500/50 group"
            style={{
              background: 'linear-gradient(135deg, #111827 0%, #1F2937 100%)',
              borderColor: getLowStockCount() > 0 ? '#EF4444' : '#374151',
              boxShadow: getLowStockCount() > 0 ? '0 10px 25px rgba(239,68,68,0.15)' : '0 10px 25px rgba(0,0,0,0.3)'
            }}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="p-2.5 rounded-lg" style={{ backgroundColor: 'rgba(239,68,68,0.15)' }}>
                <svg className="w-6 h-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              {getLowStockCount() > 0 && (
                <span className="text-xs px-2.5 py-1 rounded-full font-medium animate-pulse" style={{ backgroundColor: 'rgba(239,68,68,0.2)', color: '#EF4444' }}>
                  ⚠️ Προσοχή
                </span>
              )}
            </div>
            <p className="text-gray-400 text-sm mb-1">Χαμηλό Απόθεμα</p>
            <span className={`text-3xl font-bold ${getLowStockCount() > 0 ? 'text-red-400' : 'text-white'}`}>{getLowStockCount()}</span>
          </div>

          {/* Categories Card */}
          <div
            className="rounded-xl p-5 border transition-all duration-300 hover:border-violet-500/50 group"
            style={{
              background: 'linear-gradient(135deg, #111827 0%, #1F2937 100%)',
              borderColor: '#374151',
              boxShadow: '0 10px 25px rgba(0,0,0,0.3)'
            }}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="p-2.5 rounded-lg" style={{ backgroundColor: 'rgba(139,92,246,0.15)' }}>
                <svg className="w-6 h-6 text-violet-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
              </div>
            </div>
            <p className="text-gray-400 text-sm mb-1">Κατηγορίες</p>
            <span className="text-3xl font-bold text-white">{new Set(items.map(i => i.category).filter(Boolean)).size}</span>
          </div>
        </div>

        {/* Controls Card */}
        <div
          className="mb-6 rounded-xl p-5 border"
          style={{
            background: 'linear-gradient(135deg, #111827 0%, #1F2937 100%)',
            borderColor: '#374151',
            boxShadow: '0 10px 25px rgba(0,0,0,0.3)'
          }}
        >
          {/* Header */}
          <div className="flex items-center gap-2 mb-4">
            <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(16,185,129,0.15)' }}>
              <svg className="w-5 h-5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <h3 className="text-base lg:text-lg font-semibold text-white">Αναζήτηση & Φίλτρα</h3>
          </div>

          {/* Search and Sort Row */}
          <div className="flex flex-col lg:flex-row gap-4 mb-4">
            <div className="flex-1 relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Αναζήτηση προϊόντος ή κατηγορίας..."
                className="w-full pl-10 pr-4 py-2.5 rounded-lg border text-white focus:outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-500/30"
                style={{ backgroundColor: '#1F2937', borderColor: '#4B5563' }}
              />
              <svg className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <div className="flex gap-3">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-2.5 rounded-lg border text-white focus:outline-none focus:ring-2 focus:ring-emerald-400/30"
                style={{ backgroundColor: '#1F2937', borderColor: '#4B5563' }}
              >
                <option value="name">Ταξινόμηση: Όνομα</option>
                <option value="quantity">Ταξινόμηση: Ποσότητα</option>
                <option value="category">Ταξινόμηση: Κατηγορία</option>
              </select>
              <button
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                className="px-4 py-2.5 rounded-lg text-white transition-all hover:bg-gray-700"
                style={{ backgroundColor: '#1F2937', border: '1px solid #4B5563' }}
                title={sortOrder === 'asc' ? 'Αύξουσα' : 'Φθίνουσα'}
              >
                {sortOrder === 'asc' ? '↑' : '↓'}
              </button>
            </div>
          </div>

          {/* Action Buttons Row */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setShowLowStockOnly(!showLowStockOnly)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-white transition-all hover:scale-[1.02] ${
                showLowStockOnly ? '' : 'hover:bg-gray-700'
              }`}
              style={{
                background: showLowStockOnly
                  ? 'linear-gradient(90deg, #DC2626, #B91C1C)'
                  : '#1F2937',
                border: showLowStockOnly ? 'none' : '1px solid #4B5563'
              }}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              {showLowStockOnly ? 'Χαμηλό Απόθεμα ✓' : 'Χαμηλό Απόθεμα'}
            </button>
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium text-white transition-transform duration-200 hover:scale-[1.02]"
              style={{ background: 'linear-gradient(90deg, #10B981, #059669)' }}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Νέα Προϊόντα
            </button>
            <button
              onClick={() => {
                setBulkAddQuantities({});
                setBulkAddSearchTerm('');
                setBulkAddError('');
                setShowBulkAddDialog(true);
              }}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium text-emerald-400 transition-all hover:bg-emerald-500/10 border border-emerald-500/50 bg-[#1F2937]"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Μαζική Προσθήκη
            </button>
          </div>
        </div>

        {/* Add Form */}
        {showAddForm && (
          <div className="mb-6 rounded-xl border p-6" style={{ backgroundColor: '#111827', borderColor: '#374151', boxShadow: '0 10px 20px rgba(0,0,0,0.25)' }}>
            <h3 className="text-lg font-semibold text-white mb-5 flex items-center gap-2">
               <svg className="w-5 h-5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
               </svg>
               Νέα Προϊόντα
            </h3>
            <div className="space-y-4 mb-6">
              {newItems.map((item, index) => (
                <div key={index} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-4 p-4 rounded-xl border border-gray-700" style={{ backgroundColor: '#1F2937' }}>
                  <input
                    type="text"
                    value={item.name}
                    onChange={(e) => updateItemRow(index, 'name', e.target.value)}
                    placeholder="Όνομα *"
                    className="lg:col-span-2 px-3 py-2 rounded-lg border text-white focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                    style={{ backgroundColor: '#111827', borderColor: '#4B5563' }}
                  />
                  <input
                    type="text"
                    value={item.description}
                    onChange={(e) => updateItemRow(index, 'description', e.target.value)}
                    placeholder="Περιγραφή"
                    className="lg:col-span-3 px-3 py-2 rounded-lg border text-white focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                    style={{ backgroundColor: '#111827', borderColor: '#4B5563' }}
                  />
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={item.quantity}
                    onChange={(e) => updateItemRow(index, 'quantity', e.target.value)}
                    placeholder="Ποσότητα *"
                    required
                    className="lg:col-span-1 px-3 py-2 rounded-lg border text-white focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                    style={{ backgroundColor: '#111827', borderColor: '#4B5563' }}
                  />
                  <select
                    value={item.unit}
                    onChange={(e) => updateItemRow(index, 'unit', e.target.value)}
                    className="lg:col-span-2 px-3 py-2 rounded-lg border text-white focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                    style={{ backgroundColor: '#111827', borderColor: '#4B5563' }}
                  >
                    {units.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                  <input
                    type="number"
                    step="0.01"
                    value={item.min_quantity}
                    onChange={(e) => updateItemRow(index, 'min_quantity', e.target.value)}
                    placeholder="Ελάχ. Απόθεμα"
                    className="lg:col-span-1 px-3 py-2 rounded-lg border text-white focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                    style={{ backgroundColor: '#111827', borderColor: '#4B5563' }}
                  />
                  <select
                    value={item.category}
                    onChange={(e) => updateItemRow(index, 'category', e.target.value)}
                    className="lg:col-span-2 px-3 py-2 rounded-lg border text-white focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                    style={{ backgroundColor: '#111827', borderColor: '#4B5563' }}
                  >
                    <option value="">Κατηγορία</option>
                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  {newItems.length > 1 && (
                    <div className="lg:col-span-1 flex items-center justify-center">
                      <button
                        onClick={() => removeItemRow(index)}
                        className="p-2 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                        title="Αφαίρεση γραμμής"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={addItemRow}
                className="w-full sm:w-auto px-4 py-2 rounded-lg text-gray-300 border-2 border-dashed border-gray-600 hover:border-gray-400 hover:text-white transition-all flex items-center justify-center gap-2"
              >
                <span>+</span> Προσθήκη Γραμμής
              </button>
              <div className="flex-1"></div>
              <button
                onClick={() => {
                  setShowAddForm(false);
                  setNewItems([{ name: '', description: '', quantity: '', unit: 'τεμάχια', min_quantity: '', category: '' }]);
                }}
                className="w-full sm:w-auto px-5 py-2 rounded-lg text-gray-300 bg-gray-700 hover:bg-gray-600 transition-colors"
              >
                Ακύρωση
              </button>
              <button
                onClick={saveItems}
                className="w-full sm:w-auto px-6 py-2 rounded-lg font-medium text-white shadow-md hover:shadow-lg transition-all"
                style={{ background: 'linear-gradient(90deg, #10B981, #059669)' }}
              >
                Αποθήκευση Προϊόντων
              </button>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="rounded-xl border overflow-hidden" style={{ background: 'linear-gradient(135deg, #111827 0%, #1F2937 100%)', borderColor: '#374151', boxShadow: '0 10px 25px rgba(0,0,0,0.3)' }}>
          {/* Table Header */}
          <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: '#374151' }}>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(16,185,129,0.15)' }}>
                <svg className="w-5 h-5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Λίστα Προϊόντων</h3>
                <p className="text-xs text-gray-400">{filteredItems.length} προϊόντα</p>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{ backgroundColor: 'rgba(31,41,55,0.5)' }}>
                  <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Όνομα</th>
                  <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Περιγραφή</th>
                  <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Ποσότητα</th>
                  <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Μονάδα</th>
                  <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Ελάχ. Απόθεμα</th>
                  <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Κατηγορία</th>
                  <th className="px-5 py-3.5 text-right text-xs font-semibold text-gray-300 uppercase tracking-wider">Ενέργειες</th>
                </tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: '#374151' }}>
                {paginatedItems.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-4 py-12 text-center">
                      <div className="flex flex-col items-center">
                        <div className="w-16 h-16 mb-4 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(107,114,128,0.15)' }}>
                          <svg className="w-8 h-8 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                          </svg>
                        </div>
                        <p className="text-gray-400 font-medium mb-1">
                          {searchTerm ? 'Δεν βρέθηκαν αποτελέσματα' : 'Δεν υπάρχουν προϊόντα'}
                        </p>
                        <p className="text-gray-500 text-sm">
                          {searchTerm ? 'Δοκιμάστε διαφορετικούς όρους αναζήτησης' : 'Προσθέστε το πρώτο προϊόν στην αποθήκη σας'}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedItems.map((item) => {
                    const isLowStock = parseFloat(item.quantity) <= parseFloat(item.min_quantity || 0) && parseFloat(item.min_quantity) > 0;
                    return (
                    <tr
                      key={item.id}
                      className="hover:bg-gray-800/50 transition-colors"
                      style={{
                        borderLeft: isLowStock ? '3px solid #EF4444' : '3px solid transparent',
                        backgroundColor: isLowStock ? 'rgba(239,68,68,0.05)' : 'transparent'
                      }}
                    >
                      <td className="px-5 py-4">
                        {editingItem && editingItem.id === item.id ? (
                          <input
                            type="text"
                            value={editingItem.name}
                            onChange={(e) => setEditingItem({...editingItem, name: e.target.value})}
                            className="w-full px-2 py-1 rounded border text-white focus:outline-none"
                            style={{ backgroundColor: '#111827', borderColor: '#4B5563' }}
                          />
                        ) : (
                          <div className="flex items-center gap-2">
                            {isLowStock && (
                              <svg className="w-4 h-4 text-red-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                              </svg>
                            )}
                            <span className="text-white font-medium">{item.name}</span>
                          </div>
                        )}
                      </td>
                      <td className="px-5 py-4 text-gray-400 text-sm">
                        {editingItem && editingItem.id === item.id ? (
                          <input
                            type="text"
                            value={editingItem.description || ''}
                            onChange={(e) => setEditingItem({...editingItem, description: e.target.value})}
                            className="w-full px-2 py-1 rounded border text-white focus:outline-none"
                            style={{ backgroundColor: '#111827', borderColor: '#4B5563' }}
                          />
                        ) : (
                          <span className="text-gray-400">{item.description || '-'}</span>
                        )}
                      </td>
                      <td className="px-5 py-4">
                        {editingItem && editingItem.id === item.id ? (
                          <input
                            type="number"
                            step="0.01"
                            value={editingItem.quantity}
                            onChange={(e) => setEditingItem({...editingItem, quantity: e.target.value})}
                            className="w-20 px-2 py-1 rounded border text-white focus:outline-none"
                            style={{ backgroundColor: '#111827', borderColor: '#4B5563' }}
                          />
                        ) : (
                          <span className={`font-medium text-base ${isLowStock ? 'text-red-400' : 'text-white'}`}>
                            {item.quantity}
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-4 text-gray-300">
                        {editingItem && editingItem.id === item.id ? (
                          <select
                            value={editingItem.unit}
                            onChange={(e) => setEditingItem({...editingItem, unit: e.target.value})}
                            className="px-2 py-1 rounded border text-white focus:outline-none"
                            style={{ backgroundColor: '#111827', borderColor: '#4B5563' }}
                          >
                            {units.map(u => <option key={u} value={u}>{u}</option>)}
                          </select>
                        ) : (
                          <span className="text-gray-400">{item.unit}</span>
                        )}
                      </td>
                      <td className="px-5 py-4 text-gray-400">
                        {editingItem && editingItem.id === item.id ? (
                          <input
                            type="number"
                            step="0.01"
                            value={editingItem.min_quantity || ''}
                            onChange={(e) => setEditingItem({...editingItem, min_quantity: e.target.value})}
                            className="w-20 px-2 py-1 rounded border text-white focus:outline-none"
                            style={{ backgroundColor: '#111827', borderColor: '#4B5563' }}
                          />
                        ) : (
                          <span className="text-gray-400">{item.min_quantity || '-'}</span>
                        )}
                      </td>
                      <td className="px-5 py-4">
                        {editingItem && editingItem.id === item.id ? (
                          <select
                            value={editingItem.category || ''}
                            onChange={(e) => setEditingItem({...editingItem, category: e.target.value})}
                            className="px-2 py-1 rounded border text-white focus:outline-none"
                            style={{ backgroundColor: '#111827', borderColor: '#4B5563' }}
                          >
                            <option value="">Κατηγορία</option>
                            {categories.map(c => <option key={c} value={c}>{c}</option>)}
                          </select>
                        ) : (
                          item.category ? (
                            <span
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium"
                              style={{
                                backgroundColor: 'rgba(139,92,246,0.15)',
                                color: '#A78BFA',
                                border: '1px solid rgba(139,92,246,0.3)'
                              }}
                            >
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                              </svg>
                              {item.category}
                            </span>
                          ) : (
                            <span className="text-gray-500 text-sm">-</span>
                          )
                        )}
                      </td>
                      <td className="px-5 py-4 text-right">
                        {editingItem && editingItem.id === item.id ? (
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={saveEdit}
                              className="p-2 rounded-lg text-emerald-400 hover:bg-emerald-500/10 transition-colors"
                              title="Αποθήκευση"
                            >
                              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            </button>
                            <button
                              onClick={cancelEdit}
                              className="p-2 rounded-lg text-gray-400 hover:bg-gray-700 transition-colors"
                              title="Ακύρωση"
                            >
                              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => {
                                setItemToAddTo(item);
                                setAddQuantity('');
                                setShowAddItemsDialog(true);
                              }}
                              className="p-2 rounded-lg text-gray-400 hover:text-emerald-400 hover:bg-emerald-500/10 transition-colors"
                              title="Προσθήκη Ποσότητας"
                            >
                              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                              </svg>
                            </button>
                            <button
                              onClick={() => editItem(item)}
                              className="p-2 rounded-lg text-gray-400 hover:text-blue-400 hover:bg-blue-500/10 transition-colors"
                              title="Επεξεργασία"
                            >
                              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => confirmDelete(item)}
                              className="p-2 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                              title="Διαγραφή"
                            >
                              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  )})
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {filteredItems.length > itemsPerPage && (
            <div className="px-4 py-4 flex flex-col items-center space-y-4 border-t" style={{ borderColor: '#374151' }}>
              <div className="text-sm text-gray-400">
                Εμφάνιση {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, filteredItems.length)} από {filteredItems.length} προϊόντα
              </div>

              <div className="flex items-center space-x-2">
                <button
                  onClick={goToPreviousPage}
                  disabled={currentPage === 1}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    currentPage === 1
                      ? 'text-gray-500 cursor-not-allowed'
                      : 'text-white hover:bg-gray-700'
                  }`}
                  style={{ backgroundColor: currentPage === 1 ? '#4A5568' : '#2D3748' }}
                >
                  ← Προηγούμενη
                </button>

                <div className="flex items-center space-x-1">
                  {getPageNumbers().map((page, index) => (
                    <button
                      key={index}
                      onClick={() => typeof page === 'number' ? goToPage(page) : null}
                      disabled={typeof page !== 'number'}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        typeof page === 'number'
                          ? page === currentPage
                            ? 'text-white'
                            : 'text-gray-300 hover:text-white hover:bg-gray-700'
                          : 'text-gray-500 cursor-default'
                      }`}
                      style={{
                        backgroundColor: typeof page === 'number' && page === currentPage ? '#10B981' : '#2D3748'
                      }}
                    >
                      {page}
                    </button>
                  ))}
                </div>

                <button
                  onClick={goToNextPage}
                  disabled={currentPage >= totalPages}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    currentPage >= totalPages
                      ? 'text-gray-500 cursor-not-allowed'
                      : 'text-white hover:bg-gray-700'
                  }`}
                  style={{ backgroundColor: currentPage >= totalPages ? '#4A5568' : '#2D3748' }}
                >
                  Επόμενη →
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile overlay */}
      {isSidebarOpen && (
        <div className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setIsSidebarOpen(false)} />
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && itemToDelete && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="rounded-xl border max-w-md w-full p-6 shadow-2xl" style={{ backgroundColor: '#111827', borderColor: '#374151' }}>
            <h2 className="text-xl font-bold text-white mb-4">Επιβεβαίωση Διαγραφής</h2>
            
            <div className="mb-6">
              <p className="text-gray-300 mb-2">Είστε σίγουροι ότι θέλετε να διαγράψετε το προϊόν:</p>
              <div className="p-4 rounded-lg border border-gray-700" style={{ backgroundColor: '#1F2937' }}>
                <p className="text-white font-medium text-lg">{itemToDelete.name}</p>
                {itemToDelete.category && (
                  <p className="text-gray-400 text-sm mt-1">Κατηγορία: {itemToDelete.category}</p>
                )}
                <p className="text-gray-400 text-sm mt-1">Ποσότητα: {itemToDelete.quantity} {itemToDelete.unit}</p>
              </div>
            </div>

            <div className="flex gap-3">
              <button 
                onClick={() => deleteItem(itemToDelete.id)}
                className="flex-1 px-5 py-2.5 rounded-lg font-medium text-white transition-transform duration-200 hover:scale-[1.02]"
                style={{ backgroundColor: '#DC2626' }}
              >
                Διαγραφή
              </button>
              <button 
                onClick={() => {
                  setShowDeleteDialog(false);
                  setItemToDelete(null);
                }}
                className="flex-1 px-5 py-2.5 rounded-lg font-medium text-gray-300 transition-colors border border-gray-600 hover:bg-gray-700"
                style={{ backgroundColor: '#374151' }}
              >
                Ακύρωση
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Items Dialog */}
      {showAddItemsDialog && itemToAddTo && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="rounded-xl border max-w-md w-full p-6 shadow-2xl" style={{ backgroundColor: '#111827', borderColor: '#374151' }}>
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <svg className="w-6 h-6 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Προσθήκη Ποσότητας
            </h2>
            
            <div className="mb-6">
              <div className="p-4 rounded-lg border border-gray-700 mb-5" style={{ backgroundColor: '#1F2937' }}>
                <p className="text-white font-medium text-lg">{itemToAddTo.name}</p>
                {itemToAddTo.category && (
                  <p className="text-gray-400 text-sm mt-1">Κατηγορία: {itemToAddTo.category}</p>
                )}
                <p className="text-gray-400 text-sm mt-1">Τρέχουσα ποσότητα: <span className="text-white font-medium">{itemToAddTo.quantity} {itemToAddTo.unit}</span></p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Ποσότητα προς προσθήκη ({itemToAddTo.unit})
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={addQuantity}
                  onChange={(e) => setAddQuantity(e.target.value)}
                  placeholder="Εισάγετε ποσότητα..."
                  className="w-full px-4 py-3 rounded-lg border text-white text-lg focus:outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-500/50"
                  style={{ backgroundColor: '#111827', borderColor: '#4B5563' }}
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button 
                onClick={addQuantityToItem}
                className="flex-1 px-5 py-2.5 rounded-lg font-medium text-white transition-transform duration-200 hover:scale-[1.02] shadow-md"
                style={{ background: 'linear-gradient(90deg, #10B981, #059669)' }}
              >
                Αποθήκευση
              </button>
              <button 
                onClick={() => {
                  setShowAddItemsDialog(false);
                  setItemToAddTo(null);
                  setAddQuantity('');
                }}
                className="flex-1 px-5 py-2.5 rounded-lg font-medium text-gray-300 transition-colors border border-gray-600 hover:bg-gray-700"
                style={{ backgroundColor: '#374151' }}
              >
                Ακύρωση
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Add Quantities Dialog */}
      {showBulkAddDialog && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="rounded-xl border max-w-4xl w-full max-h-[85vh] overflow-hidden shadow-2xl flex flex-col" style={{ backgroundColor: '#111827', borderColor: '#374151' }}>
            <div className="p-6 border-b flex-shrink-0" style={{ borderColor: '#374151' }}>
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <svg className="w-6 h-6 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                  Μαζική Προσθήκη Ποσότητας
                </h2>
                <button onClick={() => { setShowBulkAddDialog(false); setBulkAddQuantities({}); setBulkAddSearchTerm(''); }} className="text-gray-400 hover:text-white transition-colors">
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              <p className="text-gray-400 text-sm mb-4">Αναζητήστε και εισάγετε τις ποσότητες που παραλάβατε για κάθε προϊόν.</p>
              
              {/* Search Filter */}
              <div className="relative">
                <input
                  type="text"
                  value={bulkAddSearchTerm}
                  onChange={(e) => setBulkAddSearchTerm(e.target.value)}
                  placeholder="Αναζήτηση προϊόντων προς προσθήκη..."
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border text-white focus:outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-500/50"
                  style={{ backgroundColor: '#1F2937', borderColor: '#4B5563' }}
                />
                <svg className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              
              {/* Error Message */}
              {bulkAddError && (
                <div className="mt-4 p-3 rounded-lg border" style={{ backgroundColor: 'rgba(239,68,68,0.1)', borderColor: 'rgba(239,68,68,0.3)' }}>
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <p className="text-red-300 text-sm font-medium">{bulkAddError}</p>
                  </div>
                </div>
              )}
            </div>
            
            <div className="p-6 overflow-y-auto flex-1 min-h-0">
              <div className="space-y-3">
                {(() => {
                  const filteredItems = items.filter(item =>
                    item.name.toLowerCase().includes(bulkAddSearchTerm.toLowerCase()) ||
                    (item.category && item.category.toLowerCase().includes(bulkAddSearchTerm.toLowerCase()))
                  );
                  
                  return (
                    <>
                      {filteredItems.map((item) => (
                        <div key={item.id} className="flex flex-col sm:flex-row sm:items-center gap-4 p-4 rounded-xl border border-gray-700 transition-colors hover:border-gray-500" style={{ backgroundColor: '#1F2937' }}>
                          <div className="flex-1">
                            <p className="text-white font-medium">{item.name}</p>
                            <p className="text-gray-400 text-sm mt-0.5">
                              Τρέχουσα ποσότητα: <span className="text-gray-300">{item.quantity}</span> {item.unit}
                              {item.category && <span className="ml-2 px-2 py-0.5 rounded text-[10px] bg-gray-700 text-gray-300">{item.category}</span>}
                            </p>
                          </div>
                          <div className="flex items-center gap-3">
                            <label className="text-gray-400 text-sm whitespace-nowrap">
                              + Προσθήκη:
                            </label>
                            <div className="relative">
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={bulkAddQuantities[item.id] || ''}
                                onChange={(e) => setBulkAddQuantities(prev => ({
                                  ...prev,
                                  [item.id]: e.target.value
                                }))}
                                placeholder="0"
                                className={`w-28 pl-4 pr-8 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-emerald-400/30 text-white font-medium ${
                                  bulkAddQuantities[item.id] ? 'bg-[#111827] border-emerald-500/50' : 'bg-[#111827] border-gray-600'
                                }`}
                              />
                              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-xs">
                                {item.unit === 'τεμάχια' ? 'τμχ' : item.unit}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </>
                  );
                })()}
              </div>
            </div>

            <div className="p-6 border-t flex-shrink-0 flex gap-3 bg-[#111827] rounded-b-xl" style={{ borderColor: '#374151' }}>
              <button 
                onClick={handleBulkAddQuantities}
                className="flex-1 px-5 py-3 rounded-lg font-medium text-white transition-transform duration-200 hover:scale-[1.02] shadow-md"
                style={{ background: 'linear-gradient(90deg, #10B981, #059669)' }}
              >
                Αποθήκευση Αλλαγών
              </button>
              <button 
                onClick={() => {
                  setShowBulkAddDialog(false);
                  setBulkAddQuantities({});
                  setBulkAddSearchTerm('');
                  setBulkAddError('');
                }}
                className="flex-1 px-5 py-3 rounded-lg font-medium text-gray-300 transition-colors border border-gray-600 hover:bg-gray-700"
                style={{ backgroundColor: '#374151' }}
              >
                Ακύρωση
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WarehousePage;